import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/app/store";
import { useNotification } from "@/context/NotificationContext";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/components/ui/use-toast";
import { createEmailTemplateAction } from "@/modules/emailManagement/features/EmailTemplateManagement/slices/emailTemplateSlice";
import {
  EmailTemplateAttachmentValue,
  EmailTemplateFormData,
  EmailTemplateFormSchema,
} from "@/modules/emailManagement/features/EmailTemplateManagement/hooks/emailTemplateFormSchema";

export function useCreateEmailTemplate() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { toast } = useToast();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isAttachmentPickerOpen, setIsAttachmentPickerOpen] = useState(false);

  const defaultValues: EmailTemplateFormData = {
    templateName: "",
    emailSubject: "",
    emailContent: "",
    categories: [],
    attachments: [],
  };

  const form = useForm<EmailTemplateFormData>({
    resolver: zodResolver(EmailTemplateFormSchema),
    defaultValues,
    mode: "onChange",
  });

  const openAttachmentPicker = useCallback(
    () => setIsAttachmentPickerOpen(true),
    [],
  );
  const closeAttachmentPicker = useCallback(
    () => setIsAttachmentPickerOpen(false),
    [],
  );

  const handleAttachmentsSelected = useCallback(
    (
      files: Array<{
        fileUuid: string;
        fileUrl: string;
        publicUrl: string;
        name: string;
        originalFilename?: string;
      }>,
    ) => {
      if (files.length === 0) {
        setIsAttachmentPickerOpen(false);
        return;
      }
      const current = form.getValues("attachments") ?? [];
      const existingUuids = new Set(current.map((a) => a.fileUuid));
      const additions: EmailTemplateAttachmentValue[] = files
        .filter((file) => !existingUuids.has(file.fileUuid))
        .map((file) => ({
          fileUuid: file.fileUuid,
          fileName: file.originalFilename || file.name,
          publicUrl: file.publicUrl,
        }));
      form.setValue("attachments", [...current, ...additions], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setIsAttachmentPickerOpen(false);
    },
    [form],
  );

  const handleRemoveAttachment = useCallback(
    (fileUuid: string) => {
      const current = form.getValues("attachments") ?? [];
      form.setValue(
        "attachments",
        current.filter((a) => a.fileUuid !== fileUuid),
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        },
      );
    },
    [form],
  );

  const submitForm = useCallback(
    async (values: EmailTemplateFormData) => {
      try {
        setIsLoading(true);

        const categoryIds = (values.categories || []).map((c) => c.id);
        const attachmentFileUuids = (values.attachments || []).map(
          (a) => a.fileUuid,
        );

        const requestData = {
          templateName: values.templateName,
          emailSubject: values.emailSubject,
          emailContent: values.emailContent,
          categoryIds: categoryIds,
          attachmentFileUuids,
        };

        dispatch(
          createEmailTemplateAction(requestData, () => {
            showNotification(
              "Success!",
              "Email template has been successfully created!",
              "success",
            );
            navigate(`/admin/email-templates`);
          }),
        );
      } catch (error) {
        console.error("Error saving email template:", error);
        if (error instanceof Error) {
          toast({
            title: "Error",
            description: error.message || "Failed to save",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: "An unknown error occurred while saving.",
            variant: "destructive",
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, toast, navigate, showNotification],
  );

  const handleCancelCreateEmailTemplate = (e: React.FormEvent) => {
    e.preventDefault();

    navigate("/admin/email-templates");
  };

  return {
    form,
    submitForm,
    handleCancelCreateEmailTemplate,
    isLoading,
    isAttachmentPickerOpen,
    openAttachmentPicker,
    closeAttachmentPicker,
    handleAttachmentsSelected,
    handleRemoveAttachment,
  };
}
